import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import {
  FaInbox,
  FaWhatsapp,
  FaPaperPlane,
  FaRobot,
  FaUserShield,
  FaHandPaper,
  FaUndo,
  FaExclamationCircle,
  FaKey,
} from "react-icons/fa";
import { db } from "../../firebase";
import { useBuilding } from "../../context/BuildingContext";
import { fetchUsersByField, tolerantId } from "../../utils/firestoreHelpers";
import { devWarn } from "../../utils/devLog";
import { useToast } from "../../components/Toast";
import EmptyState from "../../components/EmptyState";
import Loader from "../../components/Loader";
import { whatsappSetThreadMode, whatsappSendManualReply, whatsappMarkThreadRead, whatsappSendPasswordResetLink } from "../../utils/habiApi";
import { useAppDialog } from "../../components/AppDialog";
import {
  colors,
  spacing,
  typography,
  radius,
  shadows,
  pageContainer,
  pageTitle,
  pageSubtitle,
} from "../../utils/designTokens";

/**
 * Bandeja de WhatsApp con toma de control humano (takeover).
 *
 * Dos paneles: a la izquierda la lista de conversaciones (coleccion
 * `whatsapp_threads`, una por contacto, en tiempo real); a la derecha el hilo
 * de mensajes del contacto seleccionado (coleccion `messages` filtrada por
 * `contactPhone`) con caja para responder a mano.
 *
 * - `scope="building"`: admin de edificio, ve solo su edificio (BuildingContext).
 * - `scope="all"`: SuperAdmin, ve todas las conversaciones.
 *
 * El humano puede "Tomar control" (pausa a Habi en ese chat) o "Devolver a la
 * IA". Toda la escritura va por callables (Cloud Functions); las reglas dejan
 * `whatsapp_threads` read-only para el cliente.
 */

const WINDOW_MS = 24 * 60 * 60 * 1000; // ventana de 24h de Meta para texto libre

/**
 * Resuelve, en el cliente, la ficha del residente por su teléfono: nombre,
 * unidad y si es propietario o inquilino. Best-effort — si no encuentra, null.
 * Replica el matcheo de teléfono UY del webhook (598… ↔ 0… ↔ local).
 * Las unidades se buscan dentro del edificio del hilo: las reglas solo dejan
 * al staff leer `apartments` de su edificio, y la consulta tiene que fijar
 * `building` para probarlo (sin edificio, solo el superadmin).
 * Las fichas, igual (reglas 0D, C10): el staff de la bandeja del edificio lee
 * fichas ajenas solo con accessBids de ese edificio (fetchUsersByField); el
 * superadmin (scope "all") las lee todas, también las que todavía no tienen
 * accessBids.
 */
async function resolveResidentCard(phone, buildingId, scope) {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return null;
  const variants = new Set([digits, `+${digits}`]);
  if (digits.startsWith("598") && digits.length > 3) {
    const local = digits.slice(3);
    variants.add(local);
    variants.add(`0${local}`);
  }
  const cand = [...variants].slice(0, 10);
  let users;
  if (scope === "building") {
    if (buildingId == null || String(buildingId).trim() === "") return null;
    users = await fetchUsersByField("celular", cand, buildingId);
  } else {
    const usnap = await getDocs(query(collection(db, "users"), where("celular", "in", cand)));
    users = usnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }
  if (!users.length) return null;
  // Preferir el doc de residente (typeuser 0).
  const resident = users.find((u) => Number(u.typeuser) === 0) || users[0];
  const full = `${resident.NombreCompleto || ""} ${resident.ApellidosCompleto || ""}`.trim();
  const name = full || resident.name || null;
  const uid = resident.userid;
  if (uid == null) return { name, unit: null, condition: null };
  const idVars = [...new Set([uid, String(uid), Number(uid)])].filter(
    (v) => v !== null && v !== undefined && !(typeof v === "number" && Number.isNaN(v))
  ).slice(0, 10);
  let unit = null;
  let condition = null;
  const conEdificio = buildingId != null && String(buildingId).trim() !== "";
  for (const [field, label] of [["owner", "Propietario"], ["tenant", "Inquilino"]]) {
    if (unit != null) break;
    try {
      // userid (≤ 2 variantes) × edificio (2 variantes): 4 disyunciones.
      const filtros = [where(field, "in", idVars)];
      if (conEdificio) filtros.push(where("building", "in", tolerantId(buildingId)));
      const asnap = await getDocs(query(collection(db, "apartments"), ...filtros));
      for (const d of asnap.docs) {
        const a = d.data();
        if (buildingId != null && String(a.building) !== String(buildingId)) continue;
        unit = a.number ?? a.numero ?? a.unit ?? a.name ?? null;
        condition = label;
        break;
      }
    } catch (_) {
      /* índice/permiso: ignorar, seguimos best-effort */
    }
  }
  return { name, unit, condition };
}

const msDe = (ts) => ts?.toMillis?.() || 0;

/**
 * Consultas del hilo de un contacto en `messages`.
 * - Superadmin (scope "all"): el hilo entero, ordenado por createdAt.
 * - Staff (scope "building", C3): las reglas le dejan leer los mensajes con el
 *   buildingId de su edificio y, de los que no tienen edificio, los de un hilo
 *   de su edificio. Una consulta por cada caso, cada una fijando los campos
 *   que mira la regla; sin orderBy (se ordena en el cliente) para no sumar
 *   índices compuestos. `buildingId == null` encuentra solo los mensajes con
 *   el campo en null explícito: el servidor lo graba y el backfill completa
 *   los viejos.
 *   TRANSITORIO: la tercera consulta (solo por contacto) es para las reglas
 *   vigentes hasta publicar 0D, que dejan al staff leer el hilo entero, también
 *   los mensajes viejos sin el campo. Con 0D se niega y se ignora; sacarla
 *   cuando 0D esté publicada y el backfill de buildingId corrido.
 */
export const consultasDelHilo = (phone, scope, buildingId) => {
  const col = collection(db, "messages");
  if (scope !== "building") {
    return [query(col, where("contactPhone", "==", phone), orderBy("createdAt", "asc"))];
  }
  if (buildingId == null || String(buildingId).trim() === "") return [];
  return [
    query(col, where("contactPhone", "==", phone), where("buildingId", "in", tolerantId(buildingId))),
    query(col, where("contactPhone", "==", phone), where("buildingId", "==", null)),
    query(col, where("contactPhone", "==", phone)),
  ];
};

/** Une las partes del hilo sin repetir y en orden cronológico. */
export const unirMensajes = (partes) => {
  const porId = new Map();
  (partes || []).forEach((p) => (p || []).forEach((m) => porId.set(m.id, m)));
  return [...porId.values()].sort((a, b) => msDe(a.createdAt) - msDe(b.createdAt));
};

const formatTime = (ts) => {
  if (!ts) return "—";
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  return date.toLocaleString("es-UY", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ConversacionesWhatsApp = ({ scope = "all" }) => {
  const { showToast } = useToast();
  const { selectedBuilding } = useBuilding();
  const buildingId = selectedBuilding?.buildingid;

  const [threads, setThreads] = useState([]);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // all | leads
  const [leadInfo, setLeadInfo] = useState(null);
  const [residentCard, setResidentCard] = useState(null); // { name, unit, condition }

  const messagesEndRef = useRef(null);

  // Variantes de buildingId (number/string) para tolerar tipos mixtos en Firestore.
  const buildingVariants = useMemo(() => {
    if (buildingId == null) return [];
    const v = new Set([buildingId, String(buildingId)]);
    if (!Number.isNaN(Number(buildingId))) v.add(Number(buildingId));
    return [...v];
  }, [buildingId]);

  // ── Listener de conversaciones (threads) en tiempo real ──
  useEffect(() => {
    const col = collection(db, "whatsapp_threads");
    setLoadingThreads(true);

    if (scope === "building" && !buildingId) {
      setThreads([]);
      setLoadingThreads(false);
      return undefined;
    }

    const qy =
      scope === "building"
        ? query(col, where("buildingId", "in", buildingVariants), orderBy("lastMessageAt", "desc"))
        : query(col, orderBy("lastMessageAt", "desc"), limit(200));

    const unsub = onSnapshot(
      qy,
      (snap) => {
        setThreads(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoadingThreads(false);
      },
      (error) => {
        console.error("whatsapp_threads onSnapshot error (puede faltar indice):", error);
        // Fallback sin orderBy (ordenamos en cliente).
        (async () => {
          try {
            const base =
              scope === "building"
                ? query(col, where("buildingId", "in", buildingVariants))
                : query(col, limit(200));
            const s = await getDocs(base);
            const list = s.docs.map((d) => ({ id: d.id, ...d.data() }));
            list.sort(
              (a, b) => (b.lastMessageAt?.toMillis?.() || 0) - (a.lastMessageAt?.toMillis?.() || 0)
            );
            setThreads(list);
          } catch (e) {
            console.error("Fallback threads load fallo:", e);
            showToast("Error cargando conversaciones", "error");
          } finally {
            setLoadingThreads(false);
          }
        })();
      }
    );
    return () => unsub();
  }, [scope, buildingId, buildingVariants, showToast]);

  // Thread "vivo" derivado de la lista (mantiene modo/needsHuman al dia).
  const liveThread = useMemo(
    () => threads.find((t) => t.id === selectedId) || null,
    [threads, selectedId]
  );

  // ── Listener de mensajes del hilo seleccionado ──
  useEffect(() => {
    if (!selectedId) {
      setMessages([]);
      return undefined;
    }
    setLoadingMessages(true);
    const scrollAlFinal = () =>
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 80);

    if (scope === "building") {
      // Staff (C3): mensajes de su edificio + mensajes sin edificio del hilo
      // (+ la consulta TRANSITORIA). Cada parte escucha por separado; si una
      // se niega (el hilo no es de su edificio, o la transitoria con 0D) se
      // muestran las otras.
      const consultas = consultasDelHilo(selectedId, scope, buildingId);
      if (consultas.length === 0) {
        setMessages([]);
        setLoadingMessages(false);
        return undefined;
      }
      const partes = consultas.map(() => null); // null: todavía sin respuesta
      const publicar = () => {
        if (partes.some((p) => p === null)) return;
        setMessages(unirMensajes(partes));
        setLoadingMessages(false);
        scrollAlFinal();
      };
      const unsubs = consultas.map((qy, i) =>
        onSnapshot(
          qy,
          (snap) => {
            partes[i] = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            publicar();
          },
          (error) => {
            // Una parte negada por reglas es esperable (ver consultasDelHilo).
            const negada = String(error?.code || "").endsWith("permission-denied");
            (negada ? devWarn : console.warn)("messages onSnapshot (parte", i, "):", error?.code || error?.message || error);
            partes[i] = [];
            publicar();
          }
        )
      );
      return () => unsubs.forEach((u) => u());
    }

    // Superadmin: el hilo completo.
    const [qy] = consultasDelHilo(selectedId, scope, buildingId);
    const unsub = onSnapshot(
      qy,
      (snap) => {
        setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoadingMessages(false);
        scrollAlFinal();
      },
      (error) => {
        console.error("messages onSnapshot error (puede faltar indice):", error);
        (async () => {
          try {
            const s = await getDocs(
              query(collection(db, "messages"), where("contactPhone", "==", selectedId))
            );
            setMessages(unirMensajes([s.docs.map((d) => ({ id: d.id, ...d.data() }))]));
          } catch (e) {
            console.error("Fallback messages load fallo:", e);
          } finally {
            setLoadingMessages(false);
          }
        })();
      }
    );
    return () => unsub();
  }, [selectedId, scope, buildingId]);

  // Si el thread seleccionado es un prospecto, traer su lead (monto + PDF).
  useEffect(() => {
    if (!liveThread || liveThread.lane !== "lead" || !liveThread.leadId) {
      setLeadInfo(null);
      return;
    }
    let cancel = false;
    (async () => {
      try {
        const s = await getDoc(doc(db, "leads", liveThread.leadId));
        if (!cancel) setLeadInfo(s.exists() ? { id: s.id, ...s.data() } : null);
      } catch (e) {
        console.warn("No pude cargar el lead:", e);
        if (!cancel) setLeadInfo(null);
      }
    })();
    return () => { cancel = true; };
  }, [liveThread]);

  // Ficha del residente (nombre / unidad / propietario|inquilino) del hilo abierto.
  useEffect(() => {
    const phone = liveThread?.phone || selectedId;
    if (!phone) {
      setResidentCard(null);
      return undefined;
    }
    let cancel = false;
    setResidentCard(null);
    // El edificio del hilo; en la bandeja del edificio, el que se está viendo
    // (los hilos listados son de ese edificio).
    const edificioDelHilo = liveThread?.buildingId ?? (scope === "building" ? buildingId : null);
    (async () => {
      try {
        const card = await resolveResidentCard(phone, edificioDelHilo, scope);
        if (!cancel) setResidentCard(card);
      } catch (e) {
        if (!cancel) setResidentCard(null);
      }
    })();
    return () => { cancel = true; };
  }, [liveThread?.phone, liveThread?.buildingId, selectedId, scope, buildingId]);

  // Marcar leído al abrir un hilo con no-leídos (whatsapp_threads es read-only
  // para el cliente → va por callable). Best-effort: el onSnapshot refleja el 0.
  useEffect(() => {
    if (!selectedId) return;
    const t = threads.find((x) => x.id === selectedId);
    if (t && t.unreadCount > 0) {
      whatsappMarkThreadRead(t.phone || selectedId);
    }
    // Solo al cambiar de hilo (no en cada refresh de threads).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // Ventana de 24h: abierta si el ultimo inbound fue hace menos de 24h.
  const windowOpen = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].direction === "inbound") {
        const ts = messages[i].createdAt?.toMillis?.() || 0;
        return !!ts && Date.now() - ts < WINDOW_MS;
      }
    }
    return false;
  }, [messages]);

  // Link para que el residente cree su contraseña (nunca escribirla en el chat).
  const { confirmDialog } = useAppDialog();
  const [sendingReset, setSendingReset] = useState(false);
  const handleSendResetLink = useCallback(async () => {
    if (!liveThread || sendingReset) return;
    const ok = await confirmDialog(
      "Le vamos a mandar al residente un link para que cree su contraseña nueva (por este chat y por email). El link vence en 1 hora.",
      { title: "Enviar link de contraseña", confirmLabel: "Enviar link" }
    );
    if (!ok) return;
    setSendingReset(true);
    try {
      const res = await whatsappSendPasswordResetLink(liveThread.phone);
      showToast(`Link enviado por WhatsApp y a ${res?.emailMasked || "su email"}.`, "success");
    } catch (e) {
      showToast(e.message || "No se pudo enviar el link.", "error");
    } finally {
      setSendingReset(false);
    }
  }, [liveThread, sendingReset, confirmDialog, showToast]);

  const handleSend = useCallback(async () => {
    const text = replyText.trim();
    if (!text || !liveThread || sending) return;
    setSending(true);
    try {
      await whatsappSendManualReply(liveThread.phone, text);
      setReplyText("");
      // El onSnapshot reflejara el mensaje y el cambio de modo a "human".
    } catch (e) {
      showToast(e.message || "No se pudo enviar la respuesta.", "error");
    } finally {
      setSending(false);
    }
  }, [replyText, liveThread, sending, showToast]);

  const handleTakeover = useCallback(
    async (action) => {
      if (!liveThread || actionLoading) return;
      setActionLoading(true);
      try {
        await whatsappSetThreadMode(liveThread.phone, action);
        showToast(
          action === "take"
            ? "Tomaste la conversación. Habi quedó en pausa en este chat."
            : "Le devolviste el control a Habi.",
          "success"
        );
      } catch (e) {
        showToast(e.message || "No se pudo cambiar el control.", "error");
      } finally {
        setActionLoading(false);
      }
    },
    [liveThread, actionLoading, showToast]
  );

  // Orden: las que necesitan humano primero, despues por ultimo mensaje.
  // Filtro "leads" (solo SuperAdmin): muestra solo prospectos.
  const sortedThreads = useMemo(() => {
    const base = filter === "leads" ? threads.filter((t) => t.lane === "lead") : threads;
    return [...base].sort((a, b) => {
      if (!!a.needsHuman !== !!b.needsHuman) return a.needsHuman ? -1 : 1;
      return (b.lastMessageAt?.toMillis?.() || 0) - (a.lastMessageAt?.toMillis?.() || 0);
    });
  }, [threads, filter]);

  if (loadingThreads && threads.length === 0) return <Loader />;

  return (
    <div style={pageContainer}>
      <div style={{ marginBottom: spacing[4] }}>
        <h1 style={pageTitle}>
          <FaWhatsapp style={{ color: "#25D366", marginRight: 8, verticalAlign: "-2px" }} />
          Bandeja de WhatsApp
        </h1>
        <p style={{ ...pageSubtitle, marginTop: 4, maxWidth: 720 }}>
          Conversaciones con residentes. Habi responde sola; podés <strong>tomar el control</strong>{" "}
          de una conversación para responder a mano y después devolvérsela.
        </p>
      </div>

      {scope === "all" && (
        <div style={{ display: "flex", gap: spacing[2], marginBottom: spacing[3] }}>
          {[
            { id: "all", label: "Todas" },
            { id: "leads", label: "Prospectos" },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              style={{ ...styles.filterBtn, ...(filter === f.id ? styles.filterBtnActive : {}) }}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      {threads.length === 0 ? (
        <EmptyState
          icon={FaInbox}
          title="No hay conversaciones todavía"
          description="Cuando lleguen mensajes de WhatsApp, las conversaciones van a aparecer acá."
        />
      ) : (
        <div style={styles.split}>
          {/* Panel izquierdo: lista de conversaciones */}
          <div style={styles.listPane}>
            {sortedThreads.map((t) => {
              const active = t.id === selectedId;
              return (
                <button
                  key={t.id}
                  onClick={() => setSelectedId(t.id)}
                  style={{
                    ...styles.threadRow,
                    background: active ? colors.primaryLighter : colors.white,
                    borderLeft: `3px solid ${active ? colors.primary : "transparent"}`,
                  }}
                >
                  <div style={styles.threadTop}>
                    <span style={styles.threadName}>{t.residentName || t.phone}</span>
                    <span style={styles.threadTime}>{formatTime(t.lastMessageAt)}</span>
                  </div>
                  <div style={styles.threadBottom}>
                    <span style={styles.threadPreview}>
                      {t.lastDirection === "outbound" ? "→ " : ""}
                      {t.lastMessageText || "—"}
                    </span>
                    {t.unreadCount > 0 && <span style={styles.unread}>{t.unreadCount}</span>}
                  </div>
                  <div style={styles.threadTags}>
                    {t.lane === "lead" && (
                      <span style={{ ...styles.tag, ...styles.tagLead }}>Prospecto</span>
                    )}
                    {t.needsHuman && (
                      <span style={{ ...styles.tag, ...styles.tagAlert }}>
                        <FaExclamationCircle size={9} /> Necesita humano
                      </span>
                    )}
                    <span
                      style={{
                        ...styles.tag,
                        ...(t.mode === "human" ? styles.tagHuman : styles.tagAi),
                      }}
                    >
                      {t.mode === "human" ? <FaUserShield size={9} /> : <FaRobot size={9} />}
                      {t.mode === "human" ? "Humano" : "Habi"}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Panel derecho: conversación */}
          <div style={styles.chatPane}>
            {!liveThread ? (
              <div style={styles.chatEmpty}>
                <FaWhatsapp size={40} color={colors.gray300} />
                <p style={{ color: colors.gray400, marginTop: spacing[3] }}>
                  Elegí una conversación de la izquierda.
                </p>
              </div>
            ) : (
              <>
                {/* Header del chat */}
                <div style={styles.chatHeader}>
                  <div>
                    <div style={styles.chatTitle}>
                      {residentCard?.name || liveThread.residentName || liveThread.phone}
                      {residentCard?.unit != null && (
                        <span style={styles.unitChip}>Unidad {residentCard.unit}</span>
                      )}
                      {residentCard?.condition && (
                        <span
                          style={{
                            ...styles.condChip,
                            ...(residentCard.condition === "Propietario" ? styles.condOwner : styles.condTenant),
                          }}
                        >
                          {residentCard.condition}
                        </span>
                      )}
                    </div>
                    <div style={styles.chatSub}>
                      {liveThread.phone}
                      {liveThread.buildingId != null && ` · Edificio ${liveThread.buildingId}`}
                      {" · "}
                      <span style={{ color: liveThread.mode === "human" ? colors.warningDark : colors.primary }}>
                        {liveThread.mode === "human" ? "Controlado por humano" : "Atiende Habi"}
                      </span>
                    </div>
                  </div>
                  {liveThread.mode === "human" ? (
                    <button
                      onClick={() => handleTakeover("release")}
                      disabled={actionLoading}
                      style={{ ...styles.actionBtn, ...styles.actionBtnGhost }}
                    >
                      <FaUndo size={12} /> Devolver a la IA
                    </button>
                  ) : (
                    <button
                      onClick={() => handleTakeover("take")}
                      disabled={actionLoading}
                      style={{ ...styles.actionBtn, ...styles.actionBtnPrimary }}
                    >
                      <FaHandPaper size={12} /> Tomar control
                    </button>
                  )}
                </div>

                {/* Aviso de escalado */}
                {liveThread.needsHuman && (
                  <div style={styles.escalationBanner}>
                    <FaExclamationCircle />
                    <div>
                      <strong>Habi pidió ayuda humana.</strong>
                      {liveThread.escalationReason && <> Motivo: {liveThread.escalationReason}.</>}
                      {liveThread.handoffSummary && (
                        <div style={{ marginTop: 4, color: colors.gray700 }}>
                          {liveThread.handoffSummary}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Info del prospecto / cotización */}
                {liveThread.lane === "lead" && leadInfo && (
                  <div style={styles.leadBox}>
                    <strong>Prospecto.</strong>
                    {leadInfo.edificio ? ` Edificio: ${leadInfo.edificio}.` : ""}
                    {leadInfo.quoteAmount != null && (
                      <> {" "}Cotización estimada: $ {Number(leadInfo.quoteAmount).toLocaleString("es-UY")}/mes.</>
                    )}
                    {leadInfo.quotePdfUrl && (
                      <>
                        {" "}
                        <a href={leadInfo.quotePdfUrl} target="_blank" rel="noreferrer" style={{ color: colors.primary }}>
                          Ver PDF
                        </a>
                      </>
                    )}
                    {leadInfo.status ? ` · Estado: ${leadInfo.status}` : ""}
                  </div>
                )}

                {/* Mensajes */}
                <div style={styles.messagesArea}>
                  {loadingMessages && messages.length === 0 ? (
                    <Loader />
                  ) : (
                    messages.map((m) => <Bubble key={m.id} m={m} />)
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Caja de respuesta */}
                <div style={styles.replyBox}>
                  {!windowOpen && (
                    <div style={styles.windowHint}>
                      Pasaron más de 24 h desde el último mensaje del contacto: no se puede enviar
                      texto libre. Usá una plantilla aprobada (sección Plantillas).
                    </div>
                  )}
                  <div style={{ display: "flex", gap: spacing[2], alignItems: "flex-end" }}>
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSend();
                        }
                      }}
                      placeholder={windowOpen ? "Escribí tu respuesta…" : "Ventana de 24 h cerrada"}
                      disabled={!windowOpen || sending}
                      rows={2}
                      style={styles.textarea}
                    />
                    <button
                      onClick={handleSend}
                      disabled={!windowOpen || sending || !replyText.trim()}
                      style={{
                        ...styles.sendBtn,
                        opacity: !windowOpen || sending || !replyText.trim() ? 0.5 : 1,
                      }}
                      aria-label="Enviar respuesta"
                    >
                      <FaPaperPlane size={14} />
                    </button>
                  </div>
                  {liveThread && (liveThread.lane || "resident") === "resident" && (
                    <button
                      type="button"
                      onClick={handleSendResetLink}
                      disabled={!windowOpen || sendingReset}
                      style={{ ...styles.resetLinkBtn, opacity: !windowOpen || sendingReset ? 0.5 : 1 }}
                      title="Nunca escribas una contraseña en el chat: mandale este link y la crea él."
                    >
                      <FaKey size={11} /> {sendingReset ? "Enviando link…" : "Enviar link para restablecer contraseña"}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/** Burbuja de un mensaje. Inbound a la izquierda; outbound a la derecha. */
const Bubble = ({ m }) => {
  const isInbound = m.direction === "inbound";
  const byHuman = !isInbound && !!m.sentBy;
  // Envio de plantilla HSM: SOLO cuando hay templateMetaName (los textos
  // libres — human_reply, bot_response_freeform, validacion de comprobante,
  // etc. — llevan templateKey descriptivo pero NO son plantillas; mostrarles
  // el chip "Plantilla: human_reply" confundia en la bandeja).
  const isTemplateSend = !isInbound && !!m.templateMetaName;
  // Alertas operativas internas mandadas como texto libre: etiquetar Sistema.
  const isOpsSend = !isInbound && !m.sentBy && String(m.templateKey || "").startsWith("ops_");
  // Mensajes nuevos traen el body renderizado de la plantilla; los
  // historicos (pre-fix) quedan con el placeholder en cursiva.
  const isTemplatePlaceholder = !isInbound && !m.body;
  const text = isInbound
    ? m.body || "—"
    : m.body || `[Plantilla: ${m.templateMetaName || m.templateKey || "—"}]`;

  let sender = null;
  if (!isInbound) {
    sender = byHuman ? (
      <span style={styles.sender}>
        <FaUserShield size={9} /> Operador
      </span>
    ) : isTemplateSend || isOpsSend ? (
      <span style={styles.sender}>
        <FaRobot size={9} /> Sistema
      </span>
    ) : (
      <span style={styles.sender}>
        <FaRobot size={9} /> Habi
      </span>
    );
  }

  return (
    <div style={{ display: "flex", justifyContent: isInbound ? "flex-start" : "flex-end" }}>
      <div
        style={{
          ...styles.bubble,
          background: isInbound ? colors.gray100 : colors.primary,
          color: isInbound ? colors.gray900 : colors.white,
          borderBottomLeftRadius: isInbound ? 4 : radius.md,
          borderBottomRightRadius: isInbound ? radius.md : 4,
          fontStyle: isTemplatePlaceholder ? "italic" : "normal",
        }}
      >
        {sender}
        {isTemplateSend && m.body && (
          <div style={styles.templateChip}>
            Plantilla: {m.templateMetaName || m.templateKey}
          </div>
        )}
        <div style={{ whiteSpace: "pre-wrap", fontSize: typography.sizes.sm }}>{text}</div>
        <div
          style={{
            ...styles.bubbleTime,
            color: isInbound ? colors.gray400 : "rgba(255,255,255,0.8)",
          }}
        >
          {formatTime(m.createdAt || m.receivedAt || m.sentAt)}
        </div>
      </div>
    </div>
  );
};

const styles = {
  resetLinkBtn: {
    marginTop: spacing[2],
    background: "transparent",
    border: "none",
    color: colors.primary,
    fontSize: typography.sizes.sm,
    fontWeight: 400,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: 0,
  },
  split: {
    display: "flex",
    gap: spacing[4],
    height: "calc(100vh - 240px)",
    minHeight: 480,
  },
  listPane: {
    width: 340,
    flexShrink: 0,
    overflowY: "auto",
    border: `1px solid ${colors.gray200}`,
    borderRadius: radius.md,
    background: colors.white,
  },
  threadRow: {
    display: "block",
    width: "100%",
    textAlign: "left",
    padding: `${spacing[3]}px ${spacing[3]}px`,
    border: "none",
    borderBottom: `1px solid ${colors.gray100}`,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  threadTop: { display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" },
  threadName: {
    fontWeight: 400,
    color: colors.gray900,
    fontSize: typography.sizes.sm,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  threadTime: { fontSize: 11, color: colors.gray400, flexShrink: 0 },
  threadBottom: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
    marginTop: 2,
  },
  threadPreview: {
    fontSize: 12,
    color: colors.gray400,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  unread: {
    background: colors.primary,
    color: colors.white,
    borderRadius: radius.full,
    fontSize: 10,
    minWidth: 18,
    height: 18,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 5px",
    flexShrink: 0,
  },
  threadTags: { display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" },
  tag: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 10,
    padding: "2px 7px",
    borderRadius: radius.full,
  },
  tagAi: { background: colors.primaryLighter, color: colors.primary },
  tagHuman: { background: colors.warningLight, color: colors.warningDark },
  tagAlert: { background: colors.errorLight || "#fde8e8", color: colors.error },
  tagLead: { background: colors.successLight || "#dcfce7", color: colors.successDark || "#166534" },
  filterBtn: {
    padding: "8px 16px",
    fontSize: typography.sizes.base,
    fontWeight: 400,
    background: colors.white,
    color: colors.gray700,
    border: `1px solid ${colors.gray300}`,
    borderRadius: radius.sm,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  filterBtnActive: { background: colors.primary, color: colors.white, border: `1px solid ${colors.primary}` },
  leadBox: {
    padding: spacing[3],
    background: colors.successLight || "#dcfce7",
    color: colors.gray900,
    fontSize: typography.sizes.sm,
    borderBottom: `1px solid ${colors.gray200}`,
  },
  chatPane: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    border: `1px solid ${colors.gray200}`,
    borderRadius: radius.md,
    background: colors.white,
    overflow: "hidden",
  },
  chatEmpty: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  chatHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: spacing[3],
    borderBottom: `1px solid ${colors.gray200}`,
    gap: spacing[2],
  },
  chatTitle: {
    fontWeight: 400,
    color: colors.gray900,
    fontSize: typography.sizes.base,
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  chatSub: { fontSize: 12, color: colors.gray400, marginTop: 2 },
  unitChip: {
    fontSize: 11,
    padding: "2px 8px",
    borderRadius: radius.full,
    background: colors.gray100,
    color: colors.gray700,
  },
  condChip: {
    fontSize: 11,
    padding: "2px 8px",
    borderRadius: radius.full,
  },
  condOwner: { background: colors.primaryLighter, color: colors.primary },
  condTenant: { background: colors.warningLight, color: colors.warningDark },
  actionBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    borderRadius: radius.sm,
    fontSize: typography.sizes.sm,
    fontFamily: "inherit",
    cursor: "pointer",
    flexShrink: 0,
  },
  actionBtnPrimary: { background: colors.primary, color: colors.white, border: "none" },
  actionBtnGhost: {
    background: colors.white,
    color: colors.warningDark,
    border: `1px solid ${colors.warningDark}`,
  },
  escalationBanner: {
    display: "flex",
    gap: spacing[2],
    alignItems: "flex-start",
    padding: spacing[3],
    background: colors.warningLight,
    color: colors.warningDark,
    fontSize: typography.sizes.sm,
    borderBottom: `1px solid ${colors.gray200}`,
  },
  messagesArea: {
    flex: 1,
    overflowY: "auto",
    padding: spacing[3],
    display: "flex",
    flexDirection: "column",
    gap: spacing[2],
    background: colors.gray50,
  },
  bubble: {
    maxWidth: "75%",
    padding: `${spacing[2]}px ${spacing[3]}px`,
    borderRadius: radius.md,
    boxShadow: shadows.sm,
  },
  sender: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 10,
    opacity: 0.9,
    marginBottom: 2,
  },
  templateChip: {
    display: "inline-block",
    fontSize: 9,
    padding: "1px 6px",
    borderRadius: 8,
    background: "rgba(255,255,255,0.2)",
    marginBottom: 3,
  },
  bubbleTime: { fontSize: 10, marginTop: 3, textAlign: "right" },
  replyBox: { borderTop: `1px solid ${colors.gray200}`, padding: spacing[3] },
  windowHint: {
    fontSize: 12,
    color: colors.warningDark,
    background: colors.warningLight,
    padding: spacing[2],
    borderRadius: radius.sm,
    marginBottom: spacing[2],
  },
  textarea: {
    flex: 1,
    resize: "none",
    padding: spacing[2],
    border: `1px solid ${colors.gray300}`,
    borderRadius: radius.sm,
    fontFamily: "inherit",
    fontSize: typography.sizes.sm,
    outline: "none",
  },
  sendBtn: {
    background: colors.primary,
    color: colors.white,
    border: "none",
    borderRadius: radius.sm,
    width: 44,
    height: 44,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
  },
};

export default ConversacionesWhatsApp;
